import React, { useState } from "react";
import { Link, router, usePage } from "@inertiajs/react";
import { Menu, X } from "lucide-react";
import useFeature from "@/hooks/use-feature";
import DropdownMenu, {
  DropdownMenuItem,
} from "@/components/menus/DropdownMenu";

const baseLinks = [
  { href: "/tracks", label: "Tracks" },
  { href: "/briefs", label: "Briefs" },
  { href: "/artists", label: "Artists" },
  { href: "/contributors", label: "Contributors" },
];

const linkClasses = (active: boolean) =>
  `border-b-2 py-1 font-semibold transition-colors ${
    active
      ? "border-peach-500 text-peach-600 dark:text-peach-400"
      : "text-peach-700 dark:text-peach-100 dark:hover:text-peach-200 border-transparent hover:text-peach-600"
  }`;

const signOut = () => router.post("/users/sign_out");

interface NavbarProps {
  wide?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ wide = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const writerSplitsEnabled = useFeature("writer_splits");
  const { url, props } = usePage();
  const { user } = props as {
    user?: { first_name?: string; last_name?: string };
  };

  const currentPath = url.split("?")[0] === "/" ? "/tracks" : url.split("?")[0];
  const isActive = (href: string) =>
    currentPath === href || currentPath.startsWith(`${href}/`);

  const navLinks = [
    ...baseLinks,
    ...(writerSplitsEnabled ? [{ href: "/splits", label: "Splits" }] : []),
  ];

  const accountLinks = [{ href: "/users/edit", label: "Settings" }];

  const accountItems: DropdownMenuItem[] = [
    ...accountLinks.map((link) => ({ text: link.label, href: link.href })),
    { text: "Sign out", variant: "danger", onClick: signOut },
  ];

  const initials = `${user?.first_name?.[0] ?? ""}${
    user?.last_name?.[0] ?? ""
  }`.toUpperCase();

  const logo = (
    <Link href="/" className="shrink-0" onClick={() => setIsMenuOpen(false)}>
      <img
        src="/images/stupidcleverstamp.svg"
        alt="Stupid Clever Logo"
        className="h-6.5 origin-center transform transition-transform duration-300 ease-in-out hover:scale-105"
      />
    </Link>
  );

  const mobileLink = (link: { href: string; label: string }) => (
    <Link
      key={link.href}
      href={link.href}
      onClick={() => setIsMenuOpen(false)}
      className="block w-full py-3"
    >
      <span
        className={`inline-block text-base ${linkClasses(isActive(link.href))}`}
      >
        {link.label}
      </span>
    </Link>
  );

  return (
    <nav className="bg-peach-100 dark:bg-peach-900 w-full">
      <div
        className={`mx-auto flex w-full items-center justify-between gap-8 px-4 py-5 sm:px-6 lg:px-8 ${
          wide ? "max-w-page" : "max-w-content"
        }`}
      >
        <div className="flex items-center gap-8">
          {logo}

          <div className="hidden items-center gap-6 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`text-sm ${linkClasses(isActive(link.href))}`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-8">
          <div className="hidden md:block">
            <DropdownMenu
              items={accountItems}
              renderTrigger={({ ref, props: triggerProps, open }) => (
                <button
                  ref={ref}
                  {...triggerProps}
                  type="button"
                  aria-label="Account menu"
                  aria-expanded={open}
                  className="bg-peach-700 text-peach-50 dark:bg-peach-100 dark:text-peach-900 hover:bg-peach-800 dark:hover:bg-peach-50 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors hover:cursor-pointer"
                >
                  {initials}
                </button>
              )}
            />
          </div>

          <button
            onClick={() => setIsMenuOpen(true)}
            className="text-peach-700 hover:text-peach-600 dark:text-peach-100 dark:hover:text-peach-200 md:hidden"
            aria-label="Open menu"
          >
            <Menu size={24} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="bg-cream-100 dark:bg-cream-900 fixed inset-0 z-50 flex flex-col md:hidden">
          <div className="bg-peach-100 dark:bg-peach-900 w-full">
            <div className="container mx-auto flex items-center justify-between gap-8 p-5">
              {logo}
              <button
                onClick={() => setIsMenuOpen(false)}
                className="text-peach-700 hover:text-peach-600 dark:text-peach-100 dark:hover:text-peach-200"
                aria-label="Close menu"
              >
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
          </div>

          <div className="container mx-auto flex flex-1 flex-col gap-1 overflow-y-auto p-5">
            {navLinks.map(mobileLink)}

            <div className="border-cream-200 dark:border-cream-800 mt-2 flex flex-col gap-1 border-t pt-3">
              {accountLinks.map(mobileLink)}

              <div className="border-cream-200 dark:border-cream-800 mt-2 border-t pt-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    signOut();
                  }}
                  className="text-danger dark:text-danger-100 block w-full py-3 text-left text-base font-semibold hover:cursor-pointer"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
